import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { StringConstants, DiariesFilterConstants, PAGE_SIZE } from '../../WorkkardDetail/constants';
import Icon from '../../../icons';
import { showDialogueBox, hideDialogueBox } from '../../../components/DialogueBox/actions';
import WorkkardSingleOptionFilter from '../../WorkkardDetail/MiddlePanelContainer/Filter/singleOptionFilter';
import DiaryCreation from "../../CreationForms/DiaryCreation";
import { getDiariesListData, changeSelectedDiary, resetListingLogData, setSelectedDiaryDetail } from "../../../containers/Diaries/actions";
import ListingComponent from "../../../components/ListingComponent";
import DiariesListingGroup from "./diariesGroup";
import { DiaryListViewLeftPanelHeaderLoader, DiaryListViewLeftPanelListingLoader } from "../../../components/SkeletonLoader";
import EmptyScreen from '../../../Emptyscreen';
import { isMobile } from '../../../common/util';
import { groupDiariesByDate } from '../util';
import { DEFAULT_PRIORITY } from '@/common/constants';
import useLiveRefresh from '../../../utils/useLiveRefresh';

const DEFAULT_STATUS   = '1';

// Helpers
const normalizePriority = (value) => value === DEFAULT_PRIORITY ? DEFAULT_PRIORITY : value;

// Sub-components
const EmptyDiaryList = () => (
    <div className="noDataFound noDataDiary noDataFoundGrey">
        <EmptyScreen name="EmptyDiary" />
    </div>
);

const DiaryListContent = React.memo(({ groupedData, selectedDiary, onItemClick, onScrollHandler }) => {
    if (!groupedData?.length) return <EmptyDiaryList />;
    const listProps = {
        data: groupedData,
        cellTemplate:    DiariesListingGroup,
        onScrollHandler,
        onItemClick,
        type:            "ul",
        classname:       'scrollArea cardActivity',
        selectedId:      selectedDiary,
    };
    return <ListingComponent {...listProps} />;
});

const LeftLayout = ({ setMobileView }) => {
    const dispatch = useDispatch();
    const { listingData, selectedDiary, isLeftLoading: isLoading } = useSelector( ({ diaries }) => diaries.listing, shallowEqual);
    const userSessionContext = useSelector(({ sessionContextOidc }) => sessionContextOidc.userView);    
    const userId = userSessionContext?.id;
    const userPermission = userSessionContext?.permissions;

    // Local state
    const [selectedPriorityFilter, setSelectedPriorityFilter] = useState(DEFAULT_PRIORITY);
    const [selectedStatusFilter, setSelectedStatusFilter] = useState(DEFAULT_STATUS);
    const [searchKeyword, setSearchKeyword] = useState('');

    // Fetch dedup tracking — avoids re-dispatching identical or overlapping requests
    const lastFetchRef = useRef(null);
    const isFetchingRef = useRef(false);

    // Memoized values
    const priorityFilterOptions = useMemo(() => Object.values(DiariesFilterConstants.Priority), []);
    const statusFilterOptions   = useMemo(() => Object.values(DiariesFilterConstants.Duration), []);

    // Base payload shared across all list fetches
    const basePayload = useMemo(() => ({
        scope       : "personal",
        page_size   : PAGE_SIZE,
        page_no     : 1,
        priority    : normalizePriority(selectedPriorityFilter),
        status      : selectedStatusFilter,
        key         : searchKeyword.trim(),
        todoOwners  : userId,
    }), [selectedPriorityFilter, searchKeyword, selectedStatusFilter]);

    const groupedData = useMemo(
        () => groupDiariesByDate(listingData?.data ?? []),
        [listingData?.data]
    );

    // Clear the in-flight guard once the loading state resolves
    useEffect(() => {
        if (!isLoading) {
            isFetchingRef.current = false;
        }
    }, [isLoading]);

    // Update selected diary in redux
    useEffect(() => {
        const firstDiaryId = groupedData[0]?.data[0]?.todo_id;
        if (firstDiaryId && !selectedDiary) {
            dispatch(changeSelectedDiary({ id: firstDiaryId }));
        }
    }, [groupedData, selectedDiary, dispatch]);

    useEffect(() => {
        if (listingData?.data?.length > 0 && selectedDiary) {
            dispatch(setSelectedDiaryDetail({ userPermission }));
        } else {
            dispatch(resetListingLogData());
        }
    }, [selectedDiary, listingData?.data, userPermission, dispatch]);

    // Handlers
    const fetchList = useCallback((overrides = {}) => {
        const payload = { ...basePayload, ...overrides };
        const signature = JSON.stringify(payload);

        // Skip if identical to the last dispatched request
        if (lastFetchRef.current === signature) return;
        // Skip if a request is already in flight (e.g. Enter keydown followed by icon click)
        if (isFetchingRef.current) return;

        lastFetchRef.current = signature;
        isFetchingRef.current = true;
        dispatch(getDiariesListData(payload));
    }, [dispatch, basePayload]);

    // A live diary event refetches the list. lastFetchRef is cleared first: its
    // dedupe guard exists to swallow duplicate user input, but an event from
    // another user is a genuine reason to re-run the same query.
    useLiveRefresh(['diary'], () => {
        lastFetchRef.current = null;
        fetchList();
    });

    const onAddDiaryClick = useCallback(() => {
        dispatch(showDialogueBox({
            flag: true,
            component: (
                <DiaryCreation
                    type={StringConstants.common}
                    onClose={() => dispatch(hideDialogueBox())}
                    referrer="tdo"
                />
            ),
            overlayClassName:        "modal fullViewMain",
            contentClassName:        "modalContent fullView animated faster fadeInUpx2 medium",
            shouldCloseOnOverlayClick: false,
        }));
    }, [dispatch]);

    const onApplyPriorityFilter = useCallback((value) => {
        setSelectedPriorityFilter(value);
        fetchList({ priority: normalizePriority(value), isMobileView: isMobile() });
    }, [fetchList]);

    const onApplyStatusFilter = useCallback((value) => {
        setSelectedStatusFilter(value);
        fetchList({ status: value, isMobileView: isMobile() });
    }, [fetchList]);

    const searchDiaries = useCallback(() => {
        fetchList({ isMobileView: isMobile() });
    }, [fetchList]);

    const clearSearch = useCallback(() => {
        setSearchKeyword('');
        fetchList({ key: '', isMobileView: isMobile() });
    }, [fetchList]);

    const onItemClick = useCallback((id) => {
        if (isMobile()) setMobileView(true);
        dispatch(resetListingLogData());
        dispatch(changeSelectedDiary({ id }));
    }, [dispatch, setMobileView]);

    // Scroll handler
    const onScrollHandler = useCallback(() => {
        const { count, page_number } = listingData;
        if (!count || page_number * PAGE_SIZE >= count) return;
        if (isFetchingRef.current) return;
        isFetchingRef.current = true;
        dispatch(getDiariesListData({
            ...basePayload,
            page_no:  page_number + 1,
            isUpdate: true,
        }));
    }, [dispatch, listingData, basePayload]);

    if (isLoading) {
        return (
            <div className="firstLayout diaresLayout" style={{ padding: 0 }}>
                <DiaryListViewLeftPanelHeaderLoader />
                <DiaryListViewLeftPanelListingLoader count={2} />
            </div>
        );
    }

    const hasSearch = searchKeyword.trim().length > 0;

    return (
        <div className="firstLayout diaresLayout">
            <div className="filterBlock filterMonth rowFlex">
                <div data-test="DropDowns" className="filterLeft dateSort">
                    <label>Show</label>
                    <WorkkardSingleOptionFilter
                        data-test="priorityFilter"
                        filterOptions={priorityFilterOptions}
                        onApplyFilter={onApplyPriorityFilter}
                        isSelectOption
                        isOpenRight={false}
                        selectedValue={selectedPriorityFilter}
                        styleClass="wth-5"
                    />
                    <WorkkardSingleOptionFilter
                        data-test="timeFilter"
                        filterOptions={statusFilterOptions}
                        onApplyFilter={onApplyStatusFilter}
                        isSelectOption
                        isOpenRight
                        selectedValue={selectedStatusFilter}
                        styleClass="flexGrow"
                    />
                </div>

                <div className="filterRight">
                    {/* <div data-test="Search Bar" className="diarySearch rowFlex">
                        <div className="search formGroup">
                            <input
                                type="text"
                                placeholder="Search Diaries"
                                value={searchKeyword}
                                onChange={(e) => setSearchKeyword(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter') searchDiaries(); }}
                            />
                        </div>
                        {hasSearch && (
                            <button
                                type='button'
                                className="btn-cc btn-cc__transparent"
                                onClick={clearSearch}
                                style={{ paddingRight: "28px" }}
                            >
                                <Icon name="close" className="icon clearSearch" />
                            </button>
                        )}
                        <button type="button" className="btn-cc btn-cc__transparent" onClick={searchDiaries}>
                            <Icon name="search" className="icon searchIcon" />
                        </button>
                    </div> */}

                                <div className="formGroup mrNone">
              <div className='search rowFlex'>
                <input
                    type="text"
                    placeholder="Search Diaries"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') searchDiaries(); }}
                />
                <div className="search__icon">
                  <ul className="rowFlex">
                    <li>
                      <button
                        type='button'
                        className="btn-cc btn-cc__transparent"
                        onClick={clearSearch}
                    >
                        <Icon name="close" className="icon clearSearch" />
                    </button>
                    </li>
                    <li><button type="button" className="btn-cc btn-cc__transparent" onClick={searchDiaries}>
                            <Icon name="search" className="icon searchIcon" />
                        </button></li>
                  </ul>
                </div>
              </div>
            </div>

                    <button 
                        type='button'
                        data-test="Add Diary" 
                        title="Add diary" 
                        className="btn ripple btnSqure buttonGradient mr__1 addIcon addNoteButton" 
                        onClick={onAddDiaryClick}
                    >
                        <Icon name="add" className="icon white" /><span className="ml">Diary</span>
                    </button>
                </div>
            </div>

            <div className="detailMainContainer">
                <DiaryListContent
                    groupedData={groupedData}
                    selectedDiary={selectedDiary}
                    onItemClick={onItemClick}
                    onScrollHandler={onScrollHandler}
                />
            </div>
        </div>
    );
};

export default LeftLayout;